/**
 * Human player controller: turns input intents (binds, targeting, movement) into the same
 * Command objects bots emit. DOM-free so it can be unit-tested against a plain World.
 */
import type { CastFailReason, CastResult, Command, ContentRegistry, DeepReadonly, SimQueries, UnitId, Vec2, World } from '@keystone/sim';
import { ABILITY } from '@keystone/content';
import { type UiBind, abilityForBind, angleDiff, angleTo, buildCastCommand, dodgePoint } from '../input/bindings';
import { TabCycler, type TargetCandidate, targetCandidates } from '../input/targeting';
import { pressFormId } from './prediction';

/**
 * Failures worth showing as red error text. GCD/casting get queued by the sim, and facing is
 * fixed by the face command sent alongside the cast, so those stay quiet.
 */
export const SHOWN_ERRORS: Partial<Record<CastFailReason, string>> = {
  out_of_range: 'Out of range',
  no_target: 'You have no target',
  invalid_target: 'Invalid target',
  no_resource: 'Not enough resource',
  on_cooldown: 'Not ready yet',
  silenced: 'Can’t do that while silenced',
  school_locked: 'That school is locked out',
  cannot_act: 'Can’t do that right now',
  moving: 'Can’t do that while moving',
  rooted: 'You can’t move right now',
  condition_failed: 'Not usable yet',
  locked: 'You haven’t learned that yet', // +field
};

/** +autoattack: the facing error repeats at most this often (ms). */
export const SWING_ERROR_MS = 2500;

/** WoW controls: the facing the client steers (input/controls.ts Steering.facingIntent). */
export interface SteerIntent {
  /** Facing to send (radians), or null to leave the sim's facing alone (auto-face still applies at rest). */
  facing: number | null;
}

/** What a cast press sent to the sim (for client-side prediction). */
export interface CastPress {
  abilityId: string;
  /** The exact command object queued for the next tick. */
  command: Command;
  targetId: UnitId | null;
  /** `canCast` on the state at press time (null without queries). */
  check: CastResult | null;
}

export class PlayerController {
  selectedId: UnitId | null = null;
  hoverId: UnitId | null = null;
  cursorPoint: Vec2 | null = null;
  mouseover = true;
  /** Mouse-over casting for enemy spells too (off by default, as in WoW). */
  mouseoverEnemies = false;
  /** While false (paused), ability / target intents are ignored. */
  acceptIntents = true;
  /** Turn to face a hostile target while standing still. */
  autoFace = true;
  /** When the selected enemy dies in combat, select the next best engaged enemy (WoW option, default on). */
  autoRetarget = true;
  /**
   * +multiplayer: a forced move (dash, knockback) is not a lost move: wait for it to end, then
   * resend the held direction once (a party run reads the predicted world, net/predict.ts).
   */
  waitOutForcedMoves = false;
  /** Ticks left to repeat the held move after a forced move ended (waitOutForcedMoves). */
  private resendAfterForced = 0;
  private wasForced = false;
  onError: (text: string) => void = () => {};
  /**
   * Tab candidates, best first. The session injects one that knows the camera (on-screen,
   * aim point) and line of sight; the default ranks by facing and distance only.
   */
  targeting: (world: DeepReadonly<World>, opts: { frontOnly?: boolean }) => TargetCandidate[] = (world, opts) => targetCandidates(world, this.actorId, opts);
  /** Real-time clock for the Tab memory (performance.now in the browser). */
  clock: () => number = () => 0;
  readonly cycler = new TabCycler();

  private pending: Command[] = [];
  private lastDir: Vec2 = { x: 0, y: 0 };

  constructor(
    readonly actorId: UnitId,
    private readonly content: ContentRegistry,
    private readonly queries?: Pick<SimQueries, 'canCast'>,
  ) {}

  /** Dead: release now and respawn at the checkpoint (the sim also auto-releases out of combat). */
  releaseSpirit(): void {
    this.pending.push({ type: 'interact', actorId: this.actorId, what: 'releaseSpirit' });
  }

  /**
   * Select a unit. Client-side and immediate: `selectedId` (ring, nameplate, target frame) changes
   * now; the sim's target command goes out with the next (early) tick.
   */
  select(id: UnitId | null): void {
    if (!this.acceptIntents) return;
    if (id === this.selectedId) return;
    this.selectedId = id;
    this.pending.push({ type: 'target', actorId: this.actorId, targetId: id });
  }

  /** Tab / Shift+Tab: next candidate by the cycler (see input/targeting.ts). */
  tabTarget(world: DeepReadonly<World>, reverse: boolean): void {
    if (!this.acceptIntents) return;
    const self = world.units[this.actorId];
    const next = this.cycler.next(this.targeting(world, {}), this.selectedId, reverse, this.clock(), self ? { pos: self.pos, facing: self.facing } : undefined);
    if (next) this.select(next);
  }

  /** Click / F-key selection: Tab continues from it. */
  pick(id: UnitId | null): void {
    this.select(id);
    this.cycler.note(id, this.clock());
  }

  partyTarget(world: DeepReadonly<World>, slot: number): void {
    const id = world.partyIds[slot];
    if (id) this.select(id);
  }

  /**
   * +autoattack: right-click on a hostile: target it and start auto-attack (WoW interact). The sim
   * decides the rest: a caster's staff only starts in melee range (else it is just a target).
   */
  attackUnit(world: DeepReadonly<World>, id: UnitId): void {
    if (!this.acceptIntents || !this.isHostile(world, id)) return;
    this.pick(id);
    const self = world.units[this.actorId];
    if (self && !self.moving) this.pending.push({ type: 'face', actorId: this.actorId, targetId: id });
    this.pending.push({ type: 'autoAttack', actorId: this.actorId, on: true, targetId: id });
  }

  /** +autoattack: the Attack key (WoW "Attack"): toggle auto-attack; with no hostile selected, soft-target the best enemy in front. */
  toggleAttack(world: DeepReadonly<World>): void {
    if (!this.acceptIntents) return;
    const self = world.units[this.actorId];
    if (!self?.alive) return;
    if (self.autoAttack) {
      this.pending.push({ type: 'autoAttack', actorId: this.actorId, on: false });
      return;
    }
    if (!this.hostileSelected(world)) {
      const best = this.targeting(world, { frontOnly: true })[0]?.id;
      if (!best) return this.onError('You have no target');
      this.pick(best);
    }
    this.attackUnit(world, this.selectedId!);
  }

  private lastSwingError = -Infinity;
  /** +autoattack: "Target needs to be in front of you" while a due swing waits on facing (throttled, local player only). */
  swingFeedback(world: DeepReadonly<World>, nowMs: number): void {
    const self = world.units[this.actorId];
    if (!self?.autoAttack || self.swing?.blocked !== 'facing' || nowMs - this.lastSwingError < SWING_ERROR_MS) return;
    this.lastSwingError = nowMs;
    this.onError('Target needs to be in front of you');
  }

  escape(world: DeepReadonly<World>): void {
    const self = world.units[this.actorId];
    if (self?.cast || self?.queued) this.pending.push({ type: 'cancelCast', actorId: this.actorId });
    else this.select(null);
  }

  castBind(world: DeepReadonly<World>, bind: UiBind): CastPress | null {
    const self = world.units[this.actorId];
    const cls = self?.classId ? this.content.class(self.classId) : undefined;
    const abilityId = abilityForBind(cls, bind);
    if (!self || !abilityId) return null;
    return this.castAbility(world, abilityId);
  }

  /** +jump: Space. The sim refuses it mid-air, rooted or stunned. */
  jump(): void {
    if (this.acceptIntents) this.pending.push({ type: 'jump', actorId: this.actorId });
  }

  /** +glide: open (Space held past the hop's apex, or pressed again mid-air) / fold (Space released) the glider. */
  glide(on: boolean): void {
    if (this.acceptIntents) this.pending.push({ type: 'glide', actorId: this.actorId, on });
  }

  /** Shift: dash along the held move direction (from the keys and the control scheme), else straight ahead (`facing`: the client-steered facing). */
  dodge(world: DeepReadonly<World>, moveDir: Vec2, from?: Vec2, facing?: number): CastPress | null {
    const self = world.units[this.actorId];
    const ab = this.content.ability(ABILITY.common.dodge);
    if (!self || !ab) return null;
    // `from`: a party run dashes from the predicted present position (net/predict.ts).
    return this.castAbility(world, ab.id, dodgePoint(from ?? self.pos, facing ?? self.facing, moveDir, ab.range ?? 0));
  }

  /** `point` overrides the cursor for ground-targeted abilities (Dodge sends its landing point). */
  castAbility(world: DeepReadonly<World>, abilityId: string, point?: Vec2): CastPress | null {
    const self = world.units[this.actorId];
    const pressed = this.content.ability(abilityId);
    if (!self || !pressed || !this.acceptIntents) return null;
    // A transformed button (F2) targets like its current form; the command keeps the pressed id
    // (the sim resolves the override when it applies the command, and again from the spell queue).
    const form = this.content.ability(pressFormId(this.content, world, this.actorId, pressed.id)) ?? pressed;
    const ability = form === pressed ? pressed : { ...form, id: pressed.id };
    // No hostile selected for an enemy spell: soft-target the best enemy in front.
    if (ability.targeting === 'enemy' && !this.hostileSelected(world) && !(this.mouseover && this.mouseoverEnemies && this.isHostile(world, this.hoverId))) {
      const best = this.targeting(world, { frontOnly: true })[0]?.id;
      if (best) this.pick(best);
    }
    const cmd = buildCastCommand(world, ability, {
      actorId: this.actorId,
      selectedId: this.selectedId,
      hoverId: this.hoverId,
      cursorPoint: point ?? this.cursorPoint,
      mouseover: this.mouseover,
      mouseoverEnemies: this.mouseoverEnemies,
    });
    const tId = cmd.type === 'cast' ? cmd.targetId : undefined;
    const target = tId ? world.units[tId] : undefined;
    if (target && target.id !== self.id && !self.moving && (ability.requiresFacing || ability.melee || ability.targeting === 'enemy')) {
      this.pending.push({ type: 'face', actorId: this.actorId, targetId: target.id });
    }
    let check: CastResult | null = null;
    if (this.queries && cmd.type === 'cast') {
      const r = this.queries.canCast(this.actorId, ability.id, cmd.targetId ?? null, cmd.point ?? null);
      check = r;
      // Facing is auto-fixed only while standing still; moving, a target behind you fails.
      const msg = r.ok ? undefined : r.reason === 'not_facing' && self.moving ? 'You must be facing your target' : SHOWN_ERRORS[r.reason];
      if (msg) this.onError(msg);
      // A facing failure while standing still is fixed by the face command sent alongside.
      if (!r.ok && r.reason === 'not_facing' && !self.moving) check = { ok: true, queued: false };
    }
    this.pending.push(cmd);
    return { abilityId: ability.id, command: cmd, targetId: cmd.type === 'cast' ? (cmd.targetId ?? self.targetId ?? null) : null, check };
  }

  /** Commands are waiting for the sim: queued intents, the held move keys or the steered facing changed. */
  hasWork(world: DeepReadonly<World>, moveDir: Vec2, steer?: SteerIntent): boolean {
    if (this.pending.length > 0) return true;
    const self = world.units[this.actorId];
    if (!self?.alive) return false;
    if (moveDir.x !== this.lastDir.x || moveDir.y !== this.lastDir.y) return true;
    return !!steer && steer.facing !== null && angleDiff(steer.facing, self.facing) > 0.02;
  }

  /**
   * Commands for this tick: movement changes, facing, then queued intents.
   * `steer` (WoW controls, input/controls.ts): facing is steered separately from movement; its
   * `facing` is sent instead of the move direction (null: leave the sim's facing alone). Without
   * it (camera-relative scheme) the hero faces where it runs.
   */
  drain(world: DeepReadonly<World>, moveDir: Vec2, steer?: SteerIntent): Command[] {
    const out: Command[] = [];
    const self = world.units[this.actorId];
    if (self?.alive) {
      const held = moveDir.x !== 0 || moveDir.y !== 0;
      // The sim may drop our move on its own (knockback / forced move ends, CC, a move sent
      // during CC): while a key is held, resend until the sim is moving us that way again.
      const waiting = this.waitOutForcedMoves && self.move?.mode === 'forced';
      // The forced move just ended (as the room will see it): resend the held move on the next few
      // ticks too, so an arrival a tick early (still forced: the sim ignores it) is covered.
      if (this.wasForced && !waiting && held) this.resendAfterForced = 3;
      this.wasForced = waiting;
      // +glide: in the air a move waits in jump.pending (the glider steers by it): compare with that.
      const simDir = self.jump?.pending ? (self.jump.pending.dir ?? null) : self.move?.mode === 'dir' ? self.move.dir : null;
      const simLost = held && !waiting && (!simDir || Math.abs(simDir.x - moveDir.x) > 1e-3 || Math.abs(simDir.y - moveDir.y) > 1e-3);
      const insurance = held && !waiting && this.resendAfterForced > 0;
      if (insurance) this.resendAfterForced--;
      if (moveDir.x !== this.lastDir.x || moveDir.y !== this.lastDir.y || simLost || insurance) {
        out.push({ type: 'move', actorId: this.actorId, dir: { x: moveDir.x, y: moveDir.y } });
        this.lastDir = { x: moveDir.x, y: moveDir.y };
      }
      // The sim clears targetId on release / wipe respawn: re-send our selection.
      const hasPendingTarget = this.pending.some((c) => c.type === 'target');
      if (this.selectedId && self.targetId !== this.selectedId && !hasPendingTarget && world.units[this.selectedId]?.alive) {
        out.push({ type: 'target', actorId: this.actorId, targetId: this.selectedId });
      }
      const moving = moveDir.x !== 0 || moveDir.y !== 0;
      if (steer && steer.facing !== null) {
        if (angleDiff(steer.facing, self.facing) > 0.02) out.push({ type: 'face', actorId: this.actorId, facing: steer.facing });
      } else if (moving) {
        // WoW controls keep the facing while moving (backpedal); camera-relative faces the run.
        const want = Math.atan2(moveDir.y, moveDir.x);
        if (!steer && angleDiff(want, self.facing) > 0.02) out.push({ type: 'face', actorId: this.actorId, facing: want });
      } else if (this.autoFace && this.hostileSelected(world)) {
        const t = world.units[this.selectedId!]!;
        if (self.inCombat && Math.hypot(t.pos.x - self.pos.x, t.pos.y - self.pos.y) < 50 && angleDiff(angleTo(self.pos, t.pos), self.facing) > 0.1) {
          out.push({ type: 'face', actorId: this.actorId, targetId: t.id });
        }
      }
    } else {
      this.lastDir = { x: 0, y: 0 };
    }
    // The selected enemy died: next best enemy we're fighting (auto-retarget), else clear it.
    if (this.selectedId && world.units[this.selectedId] && !world.units[this.selectedId]!.alive && world.units[this.selectedId]!.faction !== self?.faction) {
      const next = this.autoRetarget && self?.alive && self.inCombat ? this.targeting(world, {}).find((c) => c.engaged)?.id : undefined;
      this.select(next ?? null);
      if (next) this.cycler.note(next, this.clock());
    }
    out.push(...this.pending);
    this.pending = [];
    return out;
  }

  private isHostile(world: DeepReadonly<World>, id: UnitId | null): boolean {
    const self = world.units[this.actorId];
    const u = id ? world.units[id] : undefined;
    return !!self && !!u && u.alive && u.faction !== self.faction;
  }

  private hostileSelected(world: DeepReadonly<World>): boolean {
    return this.isHostile(world, this.selectedId);
  }
}
